
import ImageMeta from "@/mixins/imageMeta.js";
import photoSwipeMixin from "@/mixins/photoSwipe";
import { imageSrcSet, originalImage, isVideoMedia } from '@/utils/media';

export default {
  name: "ProjectSection",
  mixins: [ImageMeta, photoSwipeMixin],
  props: ["zone"],
  data() {
    return {
      galleryID: "project-gallery",
      photoSwipeActive: true,
      ratios: {},
      _videoDimensionCache: {},
    };
  },
  computed: {
    galleryImages() {
      return (this.zone.gallery?.data || []).map((m) => ({
        ...m,
        isVideo: isVideoMedia(m.attributes),
      }));
    },
  },
  mounted() {
    this.computeAllRatios();
  },
  methods: {
    imageSrcSet,
    thumbnailSizes(media) {
      const image = originalImage(media.attributes);
      const ratio = image.width && image.height ? image.width / image.height : 1;
      return `(min-width: 1024px) ${ratio * 8}rem, ${ratio * 6}rem`;
    },
    async computeAllRatios() {
      for (let [idx, media] of this.galleryImages.entries()) {
        const {width, height, ratio} = await this.getRatioWithDimensions(media);

        // Store ratio for layout
        this.$set(this.ratios, idx, ratio);

        // Patch dimensions into media.attributes (used later by PhotoSwipe)
        if (!media.attributes.width || !media.attributes.height) {
          this.$set(media.attributes, 'width', width);
          this.$set(media.attributes, 'height', height);
        }
      }
    },

    getImageOfSize(attributes, size) {
      return attributes.formats?.[size]?.url || attributes.url;
    },

    async getRatioWithDimensions(media) {
      let width, height;
      const attributes = media.attributes;

      // Try formats (images)
      const format = originalImage(attributes);
      if (format?.width && format?.height) {
        width = format.width;
        height = format.height;
      }

      // Try root attributes (Strapi-provided)
      if ((!width || !height) && attributes?.width && attributes?.height) {
        width = attributes.width;
        height = attributes.height;
      }

      // Try to detect from video DOM if still missing
      if ((!width || !height) && media.isVideo) {
        const dim = await this.getVideoDimensions(attributes.url);
        width = dim.width;
        height = dim.height;
      }

      if (!width || !height) {
        console.warn(`No dimensions found for media:`, media);
        width = 1280;
        height = 720;
      }

      return {
        width,
        height,
        ratio: (width / height).toFixed(14),
      };
    },

    getVideoDimensions(url) {
      if (!this._videoDimensionCache) {
        this._videoDimensionCache = {};
      }

      if (this._videoDimensionCache[url]) {
        return Promise.resolve(this._videoDimensionCache[url]);
      }

      return new Promise((resolve) => {
        const video = document.createElement("video");
        video.preload = "metadata";
        video.src = url;

        video.addEventListener("loadedmetadata", () => {
          const dimensions = {
            width: video.videoWidth || 1280,
            height: video.videoHeight || 720,
          };
          this._videoDimensionCache[url] = dimensions;
          resolve(dimensions);
        });

        video.addEventListener("error", () => {
          console.warn("Failed to load video metadata", url);
          resolve({width: 1280, height: 720});
        });

        video.load(); // trigger metadata load
      });
    },

    openGallery(i) {
      this.$nextTick(() => {
        this.initPhotoSwipeWithItems(this.galleryImages, i);
      });
    },
  },
};
